import { MouseEvent, MouseEventHandler, useCallback, useMemo, useState } from 'react';

import { useMounted } from '../../utils/hooks';

export interface Props {
  /**
   * When isLoading is true,
   * button will be disabled
   * and a loader will appear as endIcon on button
   * @default false
   */
  isLoading?: boolean;
  /**
   * If a promise is returned then
   * button will be disabled
   * and a loader will appear as endIcon on button
   * until the promise is resolved or rejected
   */
  onClick?: (event: MouseEvent<HTMLElement>) => void | Promise<any>;
}

const useButtonVM = ({ isLoading: _isLoading, onClick }: Props) => {
  const getIsMounted = useMounted();

  const [isAwaiting, setIsAwaiting] = useState(false);

  const isLoading = useMemo(() => isAwaiting || _isLoading, [_isLoading, isAwaiting]);

  const handleClick: MouseEventHandler<HTMLElement> = useCallback(
    async (e) => {
      if (!onClick) return;
      try {
        setIsAwaiting(true);
        await onClick(e);
      } finally {
        const isMounted = getIsMounted();
        if (isMounted) {
          setIsAwaiting(false);
        }
      }
    },
    [onClick, getIsMounted]
  );

  return { isLoading, handleClick };
};

export default useButtonVM;
